import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';

const fadeUp = { hidden: { opacity: 0, y: 26 }, visible: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.7, delay: i * 0.12, ease: [0.16, 1, 0.3, 1] } }) };

export default function UniversPuro() {
  return (
    <div className="bg-[#F5EFE5]" data-testid="univers-page">

      {/* Hero — photo du studio avec logo mural */}
      <section className="relative min-h-[72vh] flex items-end overflow-hidden">
        <img src="/assets/histoire-hero.webp" alt="Studio PURO — logo en relief sur mur de pierre, olivier et Reformer" className="absolute inset-0 w-full h-full object-cover object-[center_35%]" fetchPriority="high" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#493329]/70 via-[#493329]/15 to-transparent" />
        <motion.div className="relative max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 pb-16 sm:pb-20 w-full"
          initial="hidden" animate="visible" variants={{ visible: { transition: { staggerChildren: 0.12 } } }}>
          <motion.p variants={fadeUp} custom={0} className="text-[11px] uppercase tracking-[0.4em] text-[#E5C98F] font-semibold mb-4">Notre histoire</motion.p>
          <motion.h1 variants={fadeUp} custom={1} className="font-heading font-medium text-4xl sm:text-5xl lg:text-6xl text-white tracking-tight leading-[1.06] max-w-2xl mb-5 drop-shadow">
            Faire entrer une expérience différente au cœur de nos villages.
          </motion.h1>
          <motion.p variants={fadeUp} custom={2} className="font-heading italic text-lg sm:text-xl text-white/85 max-w-xl mb-8">
            « Ce qui existe dans les grandes villes doit aussi pouvoir exister au cœur de nos villages. »
          </motion.p>
          <motion.div variants={fadeUp} custom={3}>
            <a href="#histoire" data-testid="univers-cta-decouvrir">
              <Button variant="outline" className="bg-white/10 backdrop-blur border-white/50 text-white hover:bg-[#F5EFE5] hover:text-[#493329] rounded-md px-7 h-11 text-sm transition-all duration-400">
                Découvrir le studio
              </Button>
            </a>
          </motion.div>
        </motion.div>
      </section>

      {/* Histoire */}
      <section id="histoire" className="scroll-mt-20">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 py-16 sm:py-24">
          <motion.div initial={{ opacity: 0, y: 22 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }}>
            <p className="text-[11px] uppercase tracking-[0.4em] text-[#9A744C] font-semibold mb-5 text-center">L'histoire de Puro</p>
            <h2 className="font-heading font-medium text-3xl sm:text-[2.6rem] text-[#493329] tracking-tight leading-tight text-center mb-10">
              PURO est né d'une envie simple.
            </h2>
            <div className="space-y-5 text-[15px] text-[#7A6455] leading-[1.85]" data-testid="histoire-texte">
              <p>
                PURO est né de la volonté d'apporter au cœur de nos villages ce que l'on retrouve habituellement
                dans les grandes villes&nbsp;: un studio de Pilates Reformer premium, intimiste et entièrement
                pensé autour de l'expérience.
              </p>
              <p>
                Nous avons imaginé PURO comme une parenthèse. Un lieu où l'on vient prendre soin de son corps,
                progresser, se renforcer et se reconnecter à soi, dans une atmosphère chaleureuse inspirée
                de la Méditerranée.
              </p>
              <p>
                Notre ambition est de rendre le Pilates Reformer de qualité plus accessible aux habitants de
                nos villages, sans avoir à parcourir des dizaines de kilomètres pour retrouver ce niveau de
                service et d'accompagnement.
              </p>
              <p>
                Avec seulement 6 personnes par séance, nous avons choisi de privilégier la proximité,
                le suivi et la qualité du coaching.
              </p>
            </div>
            <p className="font-heading italic text-2xl text-[#493329] text-center mt-10 leading-relaxed">
              PURO, c'est le Pilates Reformer autrement.<br />
              <span className="text-lg text-[#9A744C]">Plus proche. Plus humain. Plus intimiste.</span>
            </p>
          </motion.div>
        </div>
      </section>

      {/* 3 photos officielles — arche, panier, olivier */}
      <section className="bg-[#EDE3CF]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-14 sm:py-20">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-5">
            {[
              { src: '/assets/univers-arche.webp', alt: 'Arche méditerranéenne beige et crème du studio PURO', legend: "L'architecture" },
              { src: '/assets/univers-panier.webp', alt: 'Panier en fibres naturelles, ballon terracotta et serviette brodée PURO', legend: 'Les détails' },
              { src: '/assets/univers-olivier.webp', alt: 'Grand olivier ancien du studio PURO', legend: "L'olivier" },
            ].map((img, i) => (
              <motion.figure key={i} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: '-60px' }} transition={{ duration: 0.7, delay: i * 0.14 }}>
                <div className="aspect-[4/5] rounded-lg overflow-hidden">
                  <img src={img.src} alt={img.alt} loading="lazy" className="w-full h-full object-cover img-reveal" />
                </div>
                <figcaption className="text-[10px] uppercase tracking-[0.3em] text-[#9A744C] font-semibold mt-3 text-center">{img.legend}</figcaption>
              </motion.figure>
            ))}
          </div>
        </div>
      </section>

      {/* CTA final */}
      <section className="py-16 sm:py-20 text-center px-5">
        <motion.div initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}>
          <h2 className="font-heading font-medium text-3xl sm:text-4xl text-[#493329] tracking-tight mb-6">Vivez l'expérience PURO.</h2>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link to="/register" data-testid="univers-cta-preinscription">
              <Button className="bg-[#9A744C] hover:bg-[#493329] text-white rounded-md px-8 py-3.5 h-auto text-sm btn-premium transition-all duration-400">
                Je me préinscris <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/#forfaits">
              <Button variant="outline" className="border-[#D7C4AA] text-[#493329] hover:bg-[#493329] hover:text-white rounded-md px-8 py-3.5 h-auto text-sm bg-transparent transition-all duration-400">
                Découvrir les forfaits
              </Button>
            </Link>
          </div>
        </motion.div>
      </section>
    </div>
  );
}
